























































































































// utils helpers
import mixins from '@/utils/mixins';

// mixins
import componentState from '@/mixins/state';

import { mapActions } from 'vuex';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import { ErrorManager } from '@/models/error';
import {
  QuoteTermTemplate,
  IQuoteTermTemplate,
  QuoteTermTemplateFieldError,
  QuoteTemplateTranslationLanguage,
  IQuoteTermTemplateData,
  QuoteOrganization,
  IQuoteOrganization
} from '@/models';
import { AxiosError } from 'axios';

import { ALForm } from '@/common/component.types';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import QuillPDFEditor from '@/components/common/QuillPDFEditor.vue';
import QuoteTemplateNoteField from '../common/QuoteTemplateNoteField.vue';

const baseMixins = mixins(componentState);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuoteTermFormDialog',

    components: {
      ErrorAlert,
      ALDialog,
      QuillPDFEditor,
      QuoteTemplateNoteField
    },

    data: () => ({
      valid: false,
      dialog: false,
      createForm: false,

      quoteTermTemplate: new QuoteTermTemplate(),
      quoteTermTemplateTranslation: new QuoteTermTemplate(),

      organizationRules: [(v: any) => v != null || 'Organization is required'],
      titleRules: [
        (v: any) => !!v || 'Title is required',
        (v: any) =>
          (v && v.length <= 128) || 'Title must be at most 128 characters'
      ],
      loading: false,

      errorMessage: '',
      detailErrorMessage: '',
      fieldError: new QuoteTermTemplateFieldError(),

      currentLanguage: QuoteTemplateTranslationLanguage.en,
      QuoteTemplateTranslationLanguage: QuoteTemplateTranslationLanguage,

      languagesList: [
        {
          text: 'English',
          value: QuoteTemplateTranslationLanguage.en
        },
        {
          text: 'Spanish',
          value: QuoteTemplateTranslationLanguage.es
        },
        {
          text: 'Russian',
          value: QuoteTemplateTranslationLanguage.ru
        }
      ],

      requiredRules: [(v: string) => !!v || 'This field is required']
    }),

    computed: {
      organizationsList(): IQuoteOrganization[] {
        return QuoteOrganization.getOrganizationList();
      }
    },

    methods: {
      ...mapActions({
        updateQuoteTermTemplate: 'quoteTemplate/updateQuoteTermTemplate',
        createQuoteTermTemplate: 'quoteTemplate/createQuoteTermTemplate',

        fetchQuoteTermTemplateTranslations:
          'quoteTemplate/fetchQuoteTermTemplateTranslations',
        createQuoteTermTemplateTranslation:
          'quoteTemplate/createQuoteTermTemplateTranslation',
        updateQuoteTermTemplateTranslation:
          'quoteTemplate/updateQuoteTermTemplateTranslation'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
        this.fieldError.clearErrors();
      },

      open(quoteTermTemplate: IQuoteTermTemplate, createForm: boolean) {
        try {
          this.clearAllErrors();
          this.currentLanguage = QuoteTemplateTranslationLanguage.en;

          this.quoteTermTemplate = new QuoteTermTemplate(quoteTermTemplate);
          this.quoteTermTemplateTranslation = new QuoteTermTemplate();
          this.createForm = createForm;
          this.dialog = true;
          this.setComponentState('loaded');
          this.resetFormValidation();
        } catch (err) {
          this.setComponentState('error', err);
        }
      },

      close() {
        this.dialog = false;
        this.$nextTick(() => {
          this.createForm = false;
        });
      },

      async getQuoteTemplateTranslation(
        language: QuoteTemplateTranslationLanguage
      ) {
        this.clearAllErrors();
        try {
          this.quoteTermTemplateTranslation = new QuoteTermTemplate();
          if (!this.quoteTermTemplate.id) {
            return;
          }
          await this.fetchQuoteTermTemplateTranslations(
            this.quoteTermTemplate.id
          ).then((templates: IQuoteTermTemplateData[]) => {
            const quoteTermTemplate = templates.find(template => {
              return template.language === language;
            });
            if (quoteTermTemplate) {
              this.quoteTermTemplateTranslation = new QuoteTermTemplate(
                quoteTermTemplate
              );
            }
          });
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (
              QuoteTermTemplateFieldError.isQuoteTermTemplateFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new QuoteTermTemplateFieldError(
                error.response.data
              );
            } else if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }

          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.loading = false;
        }
      },

      async save() {
        if (this.validateForm()) {
          this.clearAllErrors();
          this.loading = true;
          try {
            this.quoteTermTemplateTranslation.language = this.currentLanguage;
            switch (this.currentLanguage) {
              case QuoteTemplateTranslationLanguage.es:
              case QuoteTemplateTranslationLanguage.ru:
                await this.fetchQuoteTermTemplateTranslations(
                  this.quoteTermTemplate.id
                ).then((templates: IQuoteTermTemplateData[]) => {
                  const quoteTermTemplate = templates.find(template => {
                    return (
                      template.language ===
                      this.quoteTermTemplateTranslation.language
                    );
                  });

                  if (quoteTermTemplate) {
                    this.updateQuoteTermTemplateTranslation({
                      templateId: this.quoteTermTemplate.id,
                      translation: this.quoteTermTemplateTranslation
                    }).then((template: IQuoteTermTemplateData) => {
                      this.quoteTermTemplateTranslation = new QuoteTermTemplate(
                        template
                      );
                      this.close();
                    });
                  } else {
                    this.createQuoteTermTemplateTranslation({
                      templateId: this.quoteTermTemplate.id,
                      translation: this.quoteTermTemplateTranslation
                    }).then((template: IQuoteTermTemplateData) => {
                      this.quoteTermTemplateTranslation = new QuoteTermTemplate(
                        template
                      );
                      this.close();
                    });
                  }
                });

                break;
              case QuoteTemplateTranslationLanguage.en:
              default:
                if (this.createForm) {
                  await this.createQuoteTermTemplate(this.quoteTermTemplate);
                } else {
                  await this.updateQuoteTermTemplate(this.quoteTermTemplate);
                }
                this.close();
            }
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              if (
                QuoteTermTemplateFieldError.isQuoteTermTemplateFieldError(
                  error.response.data
                )
              ) {
                this.fieldError = new QuoteTermTemplateFieldError(
                  error.response.data
                );
              } else if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }

            this.errorMessage = ErrorManager.extractApiError(error);
          } finally {
            this.loading = false;
          }
        }
      }
    }
  });
